import { ApiError } from '@api/client'
import type { MagicPlaceStatus } from '@api/types'
import { useI18n } from '@i18n/I18nProvider'
import { Button } from '@ui/Button'
import { Icon } from '@ui/Icon'
import { useState } from 'react'
import { useMagicPlaceModeration } from './useModeration'

// Qué se puede hacer con un lugar mágico según su estado (eliminar, siempre).
export const availableActions = (status: MagicPlaceStatus): ('approve' | 'retire')[] =>
	status === 'received' ? ['approve', 'retire'] : status === 'approved' ? ['retire'] : ['approve']

// Botones de moderación (panel y mapa con sesión de gestor). Eliminar pide confirmación en la
// propia pantalla, porque no se puede deshacer.
export const ModerationActions = ({
	id,
	status,
	onDeleted,
}: {
	id: string
	status: MagicPlaceStatus
	onDeleted?: () => void
}) => {
	const { t } = useI18n()
	const { approve, retire, remove } = useMagicPlaceModeration()
	const [confirming, setConfirming] = useState(false)
	const busy = approve.isPending || retire.isPending || remove.isPending
	const failed = approve.error ?? retire.error ?? remove.error
	const errorMessage =
		failed instanceof ApiError && failed.offline ? t('common.offline') : failed ? t('magic.actionError') : null

	if (confirming)
		return (
			<div role="group" aria-label={t('magic.deleteConfirm')} className="rounded-2xl bg-damaged/10 p-3">
				<p className="mb-2 font-semibold text-damaged">{t('magic.deleteConfirm')}</p>
				<div className="flex flex-wrap gap-2">
					<Button
						className="bg-damaged text-white"
						disabled={busy}
						onClick={() => remove.mutate(id, { onSuccess: () => onDeleted?.() })}
					>
						{t('magic.deleteYes')}
					</Button>
					<Button variant="ghost" disabled={busy} onClick={() => setConfirming(false)}>
						{t('magic.cancel')}
					</Button>
				</div>
				{errorMessage && (
					<p role="alert" className="mt-2 font-semibold text-damaged">
						{errorMessage}
					</p>
				)}
			</div>
		)

	return (
		<div>
			<div className="flex flex-wrap gap-2">
				{availableActions(status).map((action) =>
					action === 'approve' ? (
						<Button key={action} disabled={busy} onClick={() => approve.mutate(id)}>
							<Icon name="check" />
							{t('magic.approve')}
						</Button>
					) : (
						<Button key={action} variant="secondary" disabled={busy} onClick={() => retire.mutate(id)}>
							{t('magic.retire')}
						</Button>
					)
				)}
				<Button variant="ghost" className="text-damaged" disabled={busy} onClick={() => setConfirming(true)}>
					{t('magic.delete')}
				</Button>
			</div>
			{errorMessage && (
				<p role="alert" className="mt-2 font-semibold text-damaged">
					{errorMessage}
				</p>
			)}
		</div>
	)
}
